/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { type HttpResponse, I18nService } from '@dm/core';
import { MO } from 'app/common/constants';
import { type Lun } from 'app/common/types/mo';
import { SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';
import { pick } from 'lodash';
import { type Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LunService } from '../../../../../../service/block-service/lun-entry/lun/lun.service';
import { LunsConst } from '../../../../../../service/block-service/lun-group/luns.const';
import { GlobalSearchService } from '../../../../global-search.service';
import { DataTypeE } from '../../../../global-search-str.service';
import { type SearchParams, HASRSSOBJECT } from '../../../const';
import { InfigoService } from '../../../infigo.service';
import { SourceEnumService } from '../../../source-enum.service';
import { RefreshComponent } from '../../RefreshComponent';

const iconMap = {
    lun: '#infigo-lun',
    Namespace: '#infigo-lun',
    clone: '#infigo-lun',
    peLun: '#infigo-vvolLUN',
    vvolLun: '#infigo-vvolLUN',
};

@Component({
    selector: 'app-lun-base-info',
    templateUrl: './lun-base-info.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LunBaseInfoComponent extends RefreshComponent {
    @Input() item;
    baseInfo;
    detail: any = {};
    loading = true;
    capacityPercentData = 0;
    threSholdList = [];
    iconMap = iconMap;

    constructor(private infigoService: InfigoService,
        private lunService: LunService,
        public str: LunsConst,
        public i18n: I18nService,
        public enumService: SourceEnumService,
        public global: GlobalSearchService,
        public changeDetectorRef: ChangeDetectorRef) {
        super();
        const I18N = i18n.get;
        const enumGet = enumService.get.bind(enumService, 'LUN');
        this.baseInfo = [
            {
                title: I18N('common.healthStatus_column'),
                key: 'HEALTHSTATUS',
                enumMap: enumGet('HEALTHSTATUS'),
                templateName: 'FlagText',
                templateParams: {
                    colorMap: enumGet('HEALTHSTATUS_COLOR'),
                },
            },
            {
                title: I18N('common.runningStatus_column'),
                key: 'RUNNINGSTATUS',
                enumMap: enumGet('RUNNINGSTATUS'),
                templateName: 'FlagText',
                templateParams: {
                    colorMap: enumGet('RUNNINGSTATUS_COLOR'),
                },
            },
            // 微存储屏蔽功能类型
            ...(FeatureConfig.IsHideLunTableBusinessColumnDef ? [] : [
                {
                    title: I18N('storage.LUN_task_view_fuctionType_column'),
                    key: 'functionType',
                    enumMap: enumGet('functionType'),
                },
            ]),
            {
                title: I18N('common.mapping_column'),
                key: 'mapped',
                enumMap: enumGet('mapped'),
            },
            // 微存储屏蔽写策略
            ...(FeatureConfig.IsHideLunTableBusinessColumnDef ? [] : [
                {
                    title: I18N('storage.lun_view_summary_cacheWritetrategy_label'),
                    key: 'WRITEPOLICY',
                    enumMap: enumGet('WRITEPOLICY'),
                },
            ]),
            {
                title: I18N('storage.lun_view_summary_belongedLunGroup_tab'),
                key: 'LunGroupName',
                templateName: 'LinkIcon',
                templateParams: {
                    link: (value): SearchParams[] | SearchParams => this.global.transform(
                        { ID: value.LunGroupId },
                        null,
                        FeatureConfig.IsReplaceStrLunWithNamespace ?
                            DataTypeE.NamespaceGroup :
                            DataTypeE.lunGroup,
                    ),
                },
            },
            // 微存储直通模式展示所属硬盘
            ...(SWITCH.IS_DIRECT_MODE ? [
                {
                    title: I18N('storage.lun_task_create_belongPool_lab'),
                    key: 'PARENTNAME',
                },
            ] : [
                {
                    title: I18N('system.ownStoragePool_label'),
                    key: 'PARENTNAME',
                },
            ]),
            // 微存储屏蔽数据保护
            ...(FeatureConfig.IsHideProtection ? [] : [
                {
                    title: I18N('common.dataProtection_title'),
                    key: 'HASRSSOBJECT',
                    templateName: 'DATAPROTECTION',
                },
            ]),
            {
                title: I18N('storage.lun_view_summary_localWWN_label'),
                key: 'WWN',
                isLongString: true,
            },
        ];
    }

    requestInfo(): Observable<HttpResponse> {
        return this.infigoService.getLunDetail(this.item.actionParams);
    }

    handleRequest(value: { data: Lun }): void {
        this.detail = value.data;
        this.detail.HASRSSOBJECT = pick(value.data.HASRSSOBJECT && JSON.parse(value.data.HASRSSOBJECT) || {}, HASRSSOBJECT);
        this.capacityPercentData = this.lunService.usageValue(value.data);
        this.updateThreShold(this.detail);
        if (value.data.ISADD2LUNGROUP !== 'false') {
            this.updateLUNGroupInfo(value.data.ID);
            return;
        }
        this.loading = false;
        this.clearTimer();
        this.updateLoading = false;
        this.changeDetectorRef.detectChanges();
    }

    updateLUNGroupInfo(id: string): void {
        this.infigoService.getAssociateLunGroup(id)
            .pipe(takeUntil(this.$destroy))
            .subscribe((res) => {
                const data = res.data || [];
                const lunGroup = data.find((i) => i.TYPE === MO.LUN_GROUP);
                this.detail.LunGroupName = lunGroup && lunGroup.NAME;
                this.detail.LunGroupId = lunGroup && lunGroup.ID;
                this.loading = false;
                this.clearTimer();
                this.updateLoading = false;
                this.changeDetectorRef.detectChanges();
            });
    }

    updateThreShold(data: { ENABLEISCSITHINLUNTHRESHOLD: string; ISCSITHINLUNTHRESHOLD: any }): void {
        this.threSholdList = data.ENABLEISCSITHINLUNTHRESHOLD === 'true' ?
            [{ value: Number(data.ISCSITHINLUNTHRESHOLD), color: '#f83' }] : [];
    }
}
